<script>
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {ref, watch} from "vue";
import {useBoardingTaskStore} from "@/stores/Boarding/BoardingTask.js";
import axios from "axios";
import BoardingTaskFilter from "@/views/Boarding/BoardingTask/Components/BoardingTaskFilter.vue";
import useValidation from "@/use/Validation";

export default {
    name: "Boardings",
    components: {BoardingTaskFilter},
    setup() {
        const {
            loading,
            total,
            itemsPerPage,
            filter,
        } = useSharedData();

        const {getStatusColor, transformFilterValues, formatedDate} = useSharedMethod();

        const {mxRules} = useValidation();
        const {$i18n} = useTools();

        const boardingTaskStore = useBoardingTaskStore();
        const securityStore = useSecurityStore();

        const headers = [
            {
                text: "...",
                align: "left",
                value: "action",
                sortable: false,
                width: "0%"
            },
            {
                text: $i18n.t("module.task_model"),
                align: "left",
                value: "name",
                sortable: true,
            },
            {
                text: $i18n.t("boarding.effective_date"),
                align: "left",
                value: "boardingEffectiveDate",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.recipient"),
                align: "left",
                value: "workerLastName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("module.worker"),
                align: "left",
                value: "boardingWorkerLastName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.instruction"),
                align: "left",
                value: "instruction",
                sortable: true,
            },
            {
                text: $i18n.t("common.status"),
                align: "left",
                value: "status",
                sortable: true,
                width: "0%"
            },
        ];

        const actions = [
            {
                title: $i18n.t('common.take_in_charge'),
                icon: 'mdi-hand-pointing-right',
                color: 'blue darken-2',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    update(item, 'TAKE_IN_CHARGE')
                },
            },
            {
                title: $i18n.t('common.terminate'),
                icon: 'mdi-hand-okay',
                color: 'teal',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    update(item, 'TERMINATE')
                },
            },
            {
                title: $i18n.t('common.reassign'),
                icon: 'mdi-reload',
                color: 'purple',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    resign(item)
                },
            },
        ];

        const searchParams = ref({
            sortBy: "boardingEffectiveDate",
            descending: false,
            page: 1,
            itemsPerPage: itemsPerPage,
        });

        function handleSearch(params) {
            loading.value = true;
            boardingTaskStore.setBoardingTasks([]);
            total.value = 0;


            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/boardingTasks', {params: transformFilterValues(filter.value)}).then((response) => {
                boardingTaskStore.setBoardingTasks(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value)
        }

        watch(() => filter.value, () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});


        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }

        const resignDialog = ref(false);
        const resignWorkerId = ref(null);
        const selectedTask = ref({});

        function resign(task) {
            selectedTask.value = task;
            resignDialog.value = true;
        }

        const taskLoading = ref(false);

        function update(task, operation) {
            taskLoading.value = true;
            axios.put(`/boardingTasks`, null, {
                params: {
                    id: task.id,
                    operation: operation,
                    workerId: resignWorkerId.value,
                }
            }).then(response => {
                boardingTaskStore.updateBoardingTask(response.data);
                resignDialog.value = false;
                resignWorkerId.value = null;
                selectedTask.value = {};
            }).finally(() => {
                taskLoading.value = false;
            })
        }

        return {
            mxRules,
            boardingTaskStore,
            securityStore,
            headers,
            actions,
            searchParams,
            loading,
            taskLoading,
            total,
            itemsPerPage,
            filter,
            resignDialog,
            resignWorkerId,
            selectedTask,
            resign,
            clearFilter,
            findAll,
            update,
            getStatusColor,
            formatedDate,
        }
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.boarding_task')"
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <boarding-task-filter v-model="filter" :title="$t('module.boarding_task')"/>
            </template>

            <template v-slot:rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :headers="headers"
                    :actions="actions"
                    :items="boardingTaskStore.boardingTasks"
                    :loading="taskLoading"
                    :total="total"
                    root-path="boarding_tasks"
                    module="BOARDING_TASK"
                >
                    <template v-slot:item.name="{ item }">
                        {{ item.name }}
                    </template>
                    <template v-slot:item.boardingEffectiveDate="{ item }">
                        <w-chip color="teal" :text="formatedDate(item.boardingEffectiveDate)"/>
                    </template>
                    <template v-slot:item.workerLastName="{ item }">
                        <w-inline-text
                            bold
                            :text="item.execution === 'ORGANIZATIONAL_CHART' && !item.workerId ? item.organizationalChartName : item.workerName"
                        />
                    </template>
                    <template v-slot:item.boardingWorkerLastName="{ item }">
                        <w-inline-text outlined :text="item.boardingWorkerName"/>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <w-chip :color="getStatusColor(`BOARDING_${item.status}`)" :text="$t(`boarding.${item.status}`)"/>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>
        <v-dialog
            v-model="resignDialog"
            persistent
            max-width="600"
            scrollable
        >
            <v-card>
                <w-toolbar color="purple" :title="`Tâche: ${selectedTask.name}`"/>
                <v-card-text>
                    <v-form ref="form" lazy-validation>
                        <v-container fluid>
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('module.worker')" required/>
                                    <str-worker-autocomplete
                                        v-model="resignWorkerId"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="resignDialog = false"/>
                    <w-btn
                        color="purple"
                        icon="mdi-reload"
                        text="Réassigner"
                        :loading="taskLoading"
                        @press="update(selectedTask, 'REASSIGN')"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>